import { useCallback, useEffect, useState } from 'react'
import { Empty, Select, Spin, Table, Tag, Typography, message } from 'antd'
import { adminApi, type PageResult } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { isAttributeSelected, type SelectedAttribute } from '../lib/attributeSearch'
import { tablePagination, useServerPagination } from './ui'

type ScopeParams = { scope: KnowledgeScope }

type KeyValueRow = {
  id: number
  key: string
  value: string
  source: string
  for_suggest: boolean
  for_relation: boolean
}

const YES_NO_OPTIONS = [
  { value: 1, label: '是' },
  { value: 0, label: '否' },
]

export type KnowledgeItemKeyValuesPreviewProps = {
  itemId: number
  scopeParams: ScopeParams
  active?: boolean
  selectable?: boolean
  selectedAttrs?: SelectedAttribute[]
  onToggleAttr?: (attr: SelectedAttribute) => void
}

export function KnowledgeItemKeyValuesPreview({
  itemId,
  scopeParams,
  active = true,
  selectable = false,
  selectedAttrs = [],
  onToggleAttr,
}: KnowledgeItemKeyValuesPreviewProps) {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<KeyValueRow[]>([])
  const [total, setTotal] = useState(0)
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [savingId, setSavingId] = useState<number | null>(null)

  const loadRows = useCallback(() => {
    if (!active || !itemId) return
    setLoading(true)
    adminApi
      .get<PageResult<KeyValueRow>>(`/admin/knowledge/items/${itemId}/key-values`, {
        params: { ...scopeParams, page, page_size: pageSize },
      })
      .then((r) => {
        setRows(r.data.items || [])
        setTotal(r.data.total || 0)
      })
      .catch(() => {
        setRows([])
        setTotal(0)
      })
      .finally(() => setLoading(false))
  }, [active, itemId, scopeParams, page, pageSize])

  useEffect(() => {
    if (!active || !itemId) {
      setRows([])
      setTotal(0)
      resetPage()
      return
    }
    loadRows()
  }, [active, itemId, loadRows])

  const patchFlags = async (
    rowId: number,
    patch: Partial<Pick<KeyValueRow, 'for_suggest' | 'for_relation'>>,
  ) => {
    setSavingId(rowId)
    try {
      const r = await adminApi.patch<KeyValueRow>(
        `/admin/knowledge/items/${itemId}/key-values/${rowId}`,
        patch,
        { params: scopeParams },
      )
      setRows((prev) => prev.map((row) => (row.id === rowId ? r.data : row)))
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败')
    } finally {
      setSavingId(null)
    }
  }

  const yesNoColumn = (
    field: 'for_suggest' | 'for_relation',
    title: string,
  ) => ({
    title,
    dataIndex: field,
    width: 76,
    align: 'left' as const,
    className: 'search-doc-preview-attrs-flag-col',
    render: (v: boolean, row: KeyValueRow) => (
      <Select
        size="small"
        className="search-doc-preview-attrs-yesno"
        value={v ? 1 : 0}
        disabled={savingId === row.id}
        options={YES_NO_OPTIONS}
        onChange={(val) => void patchFlags(row.id, { [field]: val === 1 })}
      />
    ),
  })

  if (loading && !rows.length) {
    return (
      <div className="search-doc-preview-attrs-loading">
        <Spin description="加载文档关键信息…" />
      </div>
    )
  }

  if (!loading && total === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="暂无文档关键信息"
      >
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          文档索引完成后会自动抽取字段；也可在管理后台「内容抽取」中手动触发。
        </Typography.Text>
      </Empty>
    )
  }

  return (
    <>
      {selectable ? (
        <Typography.Text type="secondary" className="search-doc-preview-attrs-pick-hint">
          点击「可关联=是」的字段行可加入「查找同字段文档」
        </Typography.Text>
      ) : null}
      <Table<KeyValueRow>
      className="search-doc-preview-attrs-table"
      size="small"
      rowKey="id"
      loading={loading}
      dataSource={rows}
      rowClassName={(row) => {
        if (!selectable || !row.for_relation) return ''
        const attr = { key: row.key, value: row.value }
        return isAttributeSelected(selectedAttrs, attr)
          ? 'search-doc-preview-attrs-row--picked'
          : 'search-doc-preview-attrs-row--pickable'
      }}
      onRow={(row) => {
        if (!selectable || !row.for_relation || !onToggleAttr) return {}
        return {
          onClick: () => onToggleAttr({ key: row.key, value: row.value }),
          style: { cursor: 'pointer' },
        }
      }}
      pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
      tableLayout="fixed"
      columns={[
        {
          title: '关键信息项',
          dataIndex: 'key',
          width: 132,
          ellipsis: true,
          render: (text: string, row: KeyValueRow) =>
            selectable && row.for_relation ? (
              <Tag
                className={
                  isAttributeSelected(selectedAttrs, { key: row.key, value: row.value })
                    ? 'search-doc-preview-attrs-pick-tag--on'
                    : 'search-doc-preview-attrs-pick-tag'
                }
                color={
                  isAttributeSelected(selectedAttrs, { key: row.key, value: row.value })
                    ? 'gold'
                    : 'default'
                }
              >
                {text}
              </Tag>
            ) : (
              text
            ),
        },
        {
          title: '关键信息值',
          dataIndex: 'value',
          width: 360,
          ellipsis: true,
          render: (text: string, row: KeyValueRow) =>
            selectable && row.for_relation ? (
              <span className="search-doc-preview-attrs-pick-value">{text}</span>
            ) : (
              text
            ),
        },
        yesNoColumn('for_suggest', '联想'),
        yesNoColumn('for_relation', '可关联'),
      ]}
    />
    </>
  )
}
